import API from '../api'

export type CustomerJobItem = {
  name: string
  no: string
  type: number
  status: number
  divisions: string
  salesPerson: string
  jobId: number
  url: string
  estimateDate: string
}

export type GetCustomerJobParams = {
  customerId: number
  page: number
  pageSize: number
  sortColumn: string
  ascending: boolean
}

export type GetCustomerJobsResponse = {
  rows: CustomerJobItem[],
  totalRows: number
}

export type BulkCopyCustomerProperty = {
  customerDisplayName: string
  customerId: number
  address1: string
  propertyId: number
}

export const getCustomerJobs = async (params: GetCustomerJobParams): Promise<GetCustomerJobsResponse> => {
  return (await API.get<GetCustomerJobsResponse>('/web/customers/jobs', { params })).data
}

export const getCustomerProperties = async (): Promise<BulkCopyCustomerProperty[]> => {
  return (await API.get<BulkCopyCustomerProperty[]>('/web/customers/properties')).data
}

export type BulkCopyJobRequest = {
  jobId: number
  recipients: {customerId: number, propertyId: number}[]
  copyInternalWorkareaNotes: boolean
}

export type BulkCopyJobResponse = {
  batchId: string
}

export const bulkCopyJob = async (request: BulkCopyJobRequest): Promise<string> => {
  return (await API.post<BulkCopyJobResponse>('/web/jobs/bulk-copy', request)).data.batchId
}

export type BulkCopyJobStatusResult = {
  batchId: string
  jobs: BulkCopyJobStatus[]
}

type BulkCopyJobStatus = {
  customerId: number
  propertyId: number
  status: string
  copiedJobId?: number
  errorMessage: string
  jobUrl: string
}

export const getBulkCopyJobStatus = async (batchId: string): Promise<BulkCopyJobStatusResult> => {
  return (await API.get<BulkCopyJobStatusResult>(`/web/jobs/bulk-copy/status/${batchId}`)).data
}

export type JobCopy = {
  id: string
  customerId: number
  customerDisplayName: string
  propertyId: number
  propertyNickname: string
  address1: string
  city: string
  state: string
  copiedJobId?: number
  jobName?: string
  jobNo?: string
  type?: number
  sold?: boolean
  updatedAt: string
  isJobDeleted: boolean
  jobUrl: string
}

type JobCopiesResponse = {
  copies: JobCopy[]
}

export const getJobCopies = async (jobId: number): Promise<JobCopy[]> => {
  return (await API.get<JobCopiesResponse>(`/web/jobs/${jobId}/copies`)).data.copies
}

export type GetJobByIdResponse = {
  jobId: number
  jobName: string
  customerName: string
  jobNo: string
  propertyAddress1: string
  propertyNickname: string
}

export const getJobById = async (jobId: number): Promise<GetJobByIdResponse> => {
  return (await API.get(`/web/jobs/${jobId}`)).data
}

export type JobDetails = {
  id: number
  type: number
  no: string
  name: string
  budgetYear: number
  salesPersonEmail: string
  estimatorEmail: string
  schedulers: string
  schedulingNotes: string
  crewNotes: string
  sold: boolean
  lost: boolean
  active: boolean
  status: number | null
  estimateDate: string
  soldDate: string | null
  soldOn: string | null
  requireClockOutNotes: boolean | null
  requireClockOutPhoto: boolean | null
}

export type UpdateJobDetailsRequest = {
  no?: string
  name: string
  budgetYear: number
  salesPersonEmail: string
  estimatorEmail: string
  schedulers: string
  schedulingNotes: string
  crewNotes: string
  sold: boolean
  lost: boolean
  active: boolean
  status: number | null
  estimateDate: string
  soldOn: string | null
  requireClockOutNotes: boolean | null
  requireClockOutPhoto: boolean | null
}

export const getJobDetails = async (jobId: number): Promise<JobDetails> => {
  return (await API.get<JobDetails>(`/web/jobs/${jobId}/details`)).data
}

export const updateJobDetails = async (jobId: number, request: UpdateJobDetailsRequest): Promise<void> => {
  await API.post(`/web/jobs/${jobId}/details`, request)
}